import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";

import UserPrefsContext from "../../context/UserPrefs/UserPrefsContext";
import MonthlyFuelCostChart from "../CostsCharts/MonthlyFuelCostChart";
import CostToOwnComparison from "../CostsCharts/CostToOwnComparison";
import CostOfOwnershipOptions from "./../CostOfOwnershipOptions/CostOfOwnershipOptions";
import RenderAssumption from "../RenderAssumption/RenderAssumption";
import {
  SALES_TAX,
  GASOLINE_PRICE_IN_DOLLARS_PER_GAL,
  DOWN_PAYMENT,
  ELECTRICITY_RATE_IN_DOLLARS_PER_KWH,
} from "../../client_customizations/data/assumptions/ASSUMPTIONS";
import { FormattedMessage, useIntl } from "react-intl";
import BreakevenChart from "../CostsCharts/BreakevenChart";
import ToolTip from "../shared/ToolTip/ToolTip";

const CostOfOwnership = ({ cars, title, comparison, usedEv }) => {
  const intl = useIntl();
  const userPrefs = useContext(UserPrefsContext);
  const [isLoading, setIsLoading] = useState(true);
  const [insuranceData, setInsuranceData] = useState(null);
  const [maintenanceData, setMaintenanceData] = useState(null);

  useEffect(() => {
    const loadInsuranceData = async (vehicle_handle) => {
      const params= {
        postcode: userPrefs.get('zipcode')
      }
      let url = new URL(`${process.env.REACT_APP_EV_INFO_API_HOST}/vehicles/${vehicle_handle}/insurance`);
      let searchParams = new URLSearchParams(params);
      url.search = searchParams;
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${process.env.REACT_APP_EV_INFO_TKN}`,
          },
        });
        if (response.status === 200) {
          const insuranceData = await response.json();
          setInsuranceData(insuranceData.yearly_insurances);
          setIsLoading(false);
        }
      }
      catch(error) {
        console.log("Failed to load insurance data.");
        console.log(error);
        setIsLoading(false);
      }
    };

    const loadMaintenanceData = async (cars) => {
       const params= {
              annualmiles: userPrefs.get('milesDrivenAnnually'),
              postcode: userPrefs.get('zipcode')
       }

       let maintenanceData = [];
       for (let i = 0; i < cars.length; i++) {
              let vehicle_handle =  cars[i].handle;
              let url = new URL(`${process.env.REACT_APP_EV_INFO_API_HOST}/vehicles/${vehicle_handle}/maintenance`);
              let searchParams = new URLSearchParams(params);
              url.search = searchParams;

              try {
                     const response = await fetch(url, {
                            method: "GET",
                            headers: {
                                   "Content-Type": "application/json",
                                   Accept: "application/json",
                                   Authorization: `Bearer ${process.env.REACT_APP_EV_INFO_TKN}`,
                            },
                     });
                     if (response.status === 200) {
                            const resultData = await response.json();
                            maintenanceData.push({ handle: vehicle_handle, maintenance: resultData.maintenance.costs });
                     }
              }
              catch(error) {
                     console.log("Failed to load maintenance data.");
                     console.log(error);
              }
       };

       setMaintenanceData(maintenanceData);
       setIsLoading(false);
}

       if(cars && cars.length > 0) {
              loadInsuranceData(cars[0].handle);
              loadMaintenanceData(cars);
       }
  }, [cars, userPrefs]);

  if (!cars || cars.length === 0) return null;

  SALES_TAX.title = intl.formatMessage({
    id: "assumption.salesTax",
    defaultMessage: "Sales Tax",
  });
  SALES_TAX.value = userPrefs.get("salesTax")
  SALES_TAX.valueAsFormatted =
    (userPrefs.get("salesTax") * 100).toFixed(2) + "%";
  GASOLINE_PRICE_IN_DOLLARS_PER_GAL.title = intl.formatMessage({
    id: "assumption.gasolinePrice",
    defaultMessage: "Price of Gasoline",
  });
  DOWN_PAYMENT.title = intl.formatMessage({
    id: "assumption.downPayment",
    defaultMessage: "Down Payment",
  });
  DOWN_PAYMENT.valueAsFormatted = intl.formatMessage({
    id: "assumption.downPaymentValue",
    defaultMessage: "10% of MSRP + Tax",
  });
  ELECTRICITY_RATE_IN_DOLLARS_PER_KWH.title = intl.formatMessage({
    id: "assumption.electricityRate",
    defaultMessage: "Electricity Rate",
  });
  ELECTRICITY_RATE_IN_DOLLARS_PER_KWH.value = userPrefs.get("electricityRate");
  ELECTRICITY_RATE_IN_DOLLARS_PER_KWH.valueAsFormatted = userPrefs.get(
    "electricityRate"
  )
    ? "$" + userPrefs.get("electricityRate") + "/kWh"
    : GASOLINE_PRICE_IN_DOLLARS_PER_GAL.valueAsFormatted;

  let GASOLINE_RATE_IN_DOLLARS_PER_GALLON = {
    title: GASOLINE_PRICE_IN_DOLLARS_PER_GAL.title,
    value: userPrefs.get("gasolinePriceInCentsPerGal")
      ? userPrefs.get("gasolinePriceInCentsPerGal") / 100
      : GASOLINE_PRICE_IN_DOLLARS_PER_GAL.value,
    valueAsFormatted: userPrefs.get("gasolinePriceInCentsPerGal")
      ? "$" + userPrefs.get("gasolinePriceInCentsPerGal") / 100 + "/gal"
      : GASOLINE_PRICE_IN_DOLLARS_PER_GAL.valueAsFormatted,
  };

  return (
    <section
      className="container-fluid"
      id="CostOfOwnership"
      style={{ paddingBottom: "0px" }}
    >
      <section className="container containerBig">
        <div className="graph-title">
          <FormattedMessage
            id="graph.title.costAnalysis"
            defaultMessage="Cost analysis"
            description="Cost analysis"
          />
          <ToolTip
            message={intl.formatMessage({
              id: "costAnalysisDisclaimer",
              defaultMessage: "Based on the miles and hours you drive.",
            })}
            id="cost_analysis_tooltip"
          />
        </div>
        <div className="row">
          <div className="col-md-4 col-lg-4" style={{ textAlign: "left" }}>
            <CostOfOwnershipOptions cars={cars} usedEv={usedEv} />
            <br />
            <div className="input-well d-none d-md-block">
              <p
                className="RenderItemTitle"
                style={{ marginBottom: 10, textAlign: "left" }}
              >
                <FormattedMessage
                  id="graph.assumptions"
                  defaultMessage="Assumptions"
                  description="Assumptions"
                />
              </p>

              <RenderAssumption assumption={SALES_TAX} />
              <RenderAssumption
                assumption={GASOLINE_RATE_IN_DOLLARS_PER_GALLON}
                tooltip="Latest gas price reported for your state, based on AAA data."
              />
              <RenderAssumption assumption={DOWN_PAYMENT} />

              <RenderAssumption
                assumption={ELECTRICITY_RATE_IN_DOLLARS_PER_KWH}
              />
              <p className="legal-disclaimer">
                <p>
                  Electricity rate is calculated as the average for six month of
                  summer off-peak and six months off-peak for NRG Rate A, the
                  cheapest rate available for your zip code.
                </p>
              </p>
            </div>
            {!comparison ? (
              <p
                style={{
                  marginTop: "30px",
                  fontFamily: "TT Norms Pro",
                  fontStyle: "normal",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: "#191919",
                }}
              >{`*We selected the ${cars[1].model} based on its proximity to the ${cars[0].model} based on brand, size, and price.`}</p>
            ) : null}
          </div>
          <div
            id="costs-chart-areas"
            aria-live="polite"
            aria-atomic="true"
            role="region"
            style={{ position: "absolute", top: "-9999px" }}
          >
            <span>
              <FormattedMessage
                id="results.updated"
                defaultMessage="Results have been updated as of "
                description="Results have been updated as of "
              />
              {new Date().toLocaleString()}.
            </span>
          </div>
          <div className="col-md-8 col-lg-8">
            <MonthlyFuelCostChart cars={cars} comparison={comparison} />
            <br />
            {!isLoading && insuranceData ? (
              <CostToOwnComparison
                cars={cars}
                hasExtraPadding
                customLegend
                comparison={comparison}
                insuranceData={insuranceData}
                maintenanceData={maintenanceData}
              />
            ) : null}
            <br />
            {cars.length <= 2 ? <BreakevenChart cars={cars} /> : null}
          </div>
        </div>
      </section>
    </section>
  );
};

export default CostOfOwnership;

CostOfOwnership.propTypes = {
  car: PropTypes.object,
};
